import { Injectable } from '@angular/core';
import {
  CatalogService,
  CustomerDepartmentAddFavoriteQualificationsRequest,
  CustomerDepartmentCreateRequest,
  CustomerDepartmentQualificationCreateRequest,
  CustomerDepartmentResponse,
  CustomerDepartmentService,
  CustomerDepartmentUpdateRequest,
  CustomerDepartmentUpdateShiftTimesRequest,
  CustomerLocationDepartmentRequirementService,
  CustomerLocationDepartmentService,
  CustomerService,
  DepartmentListItemResponse,
  EmptySuccessfulResponse,
  RequirementCategoryResponse,
  UpdateUsersRequest,
  UserItemResponse,
  GeneralApiResponse,
  CustomerDepartmentUpdateRequirementsRequest,
} from '../../../api';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { baseDeepClone } from '../../shared/helpers/base-deep-clone';
import { BaseApplicationTagType } from '../../shared/enums/base-application.enum';

@Injectable()
export class BaseCustomerDepartmentService {
  constructor(
    private readonly customerDepartmentService: CustomerDepartmentService,
    private readonly customerLocationDepartmentService: CustomerLocationDepartmentService,
    private readonly customerLocationDepartmentRequirementsService: CustomerLocationDepartmentRequirementService,
    private readonly customerService: CustomerService,
    private readonly catalogService: CatalogService
  ) {}

  getCustomerDepartmentList(customerId: number | string): Observable<DepartmentListItemResponse[]> {
    return this.customerDepartmentService
      .getCustomerDepartmentListOfDepartments({ customer: customerId as number })
      .pipe(
        map((response): DepartmentListItemResponse[] =>
          response.data?.length ? (response.data as DepartmentListItemResponse[]) : []
        )
      );
  }

  getCustomerDepartment(params: { department: number; customer: number }): Observable<
    GeneralApiResponse & {
      data?: CustomerDepartmentResponse;
    }
  > {
    return this.customerDepartmentService.getCustomerDepartmentShow(params);
  }

  getCustomerUsers(params: {
    customerId: number;
    search?: string;
    active?: boolean;
    roles?: Array<'user' | 'admin' | 'workCouncil'>;
  }): Observable<UserItemResponse[]> {
    return this.customerService
      .getCustomersUsersList({
        customer: params.customerId,
        'role[]': params.roles,
        active: params.active,
        search: params.search,
      })
      .pipe(map((response) => (response.data?.length ? response.data : [])));
  }

  updateUsersToCustomerDepartment(params: {
    department: number;
    customer: number;
    location: number;
    body: UpdateUsersRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putLocationDepartmentUpdateUsers(params);
  }

  updateMasterData(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentUpdateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putCustomerLocationDepartmentUpdate(params);
  }

  updateShiftTimes(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentUpdateShiftTimesRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putCustomerLocationDepartmentTimeShifts(params);
  }

  getAvailableRequirementCategories(tag?: BaseApplicationTagType): Observable<RequirementCategoryResponse[]> {
    return this.catalogService
      .getAppApiWebV1CatalogsRequirementListIndex({ tag })
      .pipe(map((response) => response.data.categories));
  }

  updateRequirements(params: {
    customer: number;
    location: number;
    department: number;
    body: CustomerDepartmentUpdateRequirementsRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentRequirementsService.putCustomerLocationDepartmentRequirementUpdate(params);
  }

  deleteRequirement(params: {
    department: number;
    requirement: number;
    location: number;
    customer: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentRequirementsService.deleteCustomerLocationDepartmentRequirementDelete(params);
  }

  createDepartment(
    customerId: number,
    location: number,
    body: CustomerDepartmentCreateRequest
  ): Observable<DepartmentListItemResponse> {
    if (body.primaryData.name) {
      return this.customerLocationDepartmentService
        .postCustomerDepartmentCreate({
          customer: customerId,
          location: location,
          body,
        })
        .pipe(map((response) => response.data));
    }

    return this.customerService.getCustomerGet({ customer: customerId }).pipe(
      mergeMap((result) => {
        const requestBody = baseDeepClone(body) as CustomerDepartmentCreateRequest;
        requestBody.primaryData.name = !!result.data.primaryData?.name ? result.data.primaryData?.name : '';

        return this.customerLocationDepartmentService
          .postCustomerDepartmentCreate({
            customer: customerId,
            location: location,
            body: requestBody,
          })
          .pipe(map((response) => response.data));
      })
    );
  }

  addFavouriteQualifications(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentAddFavoriteQualificationsRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.postCustomerLocationDepartmentQualificationsFavoriteAdd(params);
  }

  deleteFavouriteQualification(params: {
    department: number;
    qualification: number;
    customer: number;
    location: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.deleteCustomerLocationDepartmentQualificationsFavoriteDelete(params);
  }

  updateDepartmentQualificationDescription(params: {
    qualification: number;
    customer: number;
    department: number;
    location: number;
    body: CustomerDepartmentQualificationCreateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.postCustomerLocationDepartmentQualificationsDescriptionCreate(params);
  }
}
